import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from "../components/Navbar";
import ProviderTabs from "../components/ProviderTabs";
import { ZONES } from '../lib/zones';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PostAsset() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/categories`)
      .then(r => r.json())
      .then(data => {
        setCategories(data);
        if (data.length > 0) setFormData(prev => ({ ...prev, type: data[0].type }));
      })
      .catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    type: '',
    name: '',
    description: '',
    brand: '',
    model: '',
    year: 2024,
    capacity: 2,
    price_per_hour: 0,
    price_per_day: 0,
    location_zone: 'Punta Cana',
    pickup_address: '',
    what_included: [],
    images: []
  });
  const [uploadedPreviews, setUploadedPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleImageFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setUploadError('');
    const newUrls = [];
    const newPreviews = [];
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) {
        setUploadError(t('postAsset.fileTooLarge', 'La imagen es demasiado grande (máximo 15 MB).'));
        continue;
      }
      const body = new FormData();
      body.append('file', file);
      try {
        const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          credentials: 'include',
          body,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setUploadError(err.detail || t('postAsset.uploadImageError', 'Error al subir la imagen'));
          continue;
        }
        const data = await res.json();
        newUrls.push(data.url);
        newPreviews.push(URL.createObjectURL(file));
      } catch (err) {
        console.error("Upload error:", err);
        setUploadError(t('postAsset.uploadConnectionError'));
      }
    }
    setFormData(prev => ({ ...prev, images: [...prev.images, ...newUrls] }));
    setUploadedPreviews(prev => [...prev, ...newPreviews]);
    setUploading(false);
  };

  const removeImage = async (idx) => {
    const url = formData.images[idx];
    // Optimistic UI: quitamos del state antes del roundtrip. Si el DELETE
    // falla el archivo queda huérfano pero el asset no se referencia — se
    // limpia después con un cleanup periódico.
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
    setUploadedPreviews(prev => prev.filter((_, i) => i !== idx));
    // Extraer filename del URL (última parte de la ruta) y pedir DELETE al server.
    // El backend devuelve las URLs de upload bajo FRONTEND_URL (nginx sirve
    // /uploads/, no FastAPI) — por eso comparamos contra window.location.origin
    // (el frontend corre exactamente en ese origen) y NO contra BACKEND_URL,
    // que es un origen distinto (típicamente api.*).
    try {
      const target = new URL(url);
      if (target.origin === window.location.origin && target.pathname.startsWith('/uploads/')) {
        const filename = target.pathname.split('/').pop();
        await fetch(`${BACKEND_URL}/api/upload/image/${encodeURIComponent(filename)}`, {
          method: 'DELETE', credentials: 'include',
        });
      }
    } catch {
      // El archivo huérfano no bloquea al usuario; el cleanup periódico se encarga.
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.images.length === 0) {
      setUploadError(t('postAsset.atLeastOneImage'));
      return;
    }
    setUploadError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        // Sin este check, un 422/401/500 pasaba desapercibido y navegábamos
        // a /provider/assets como si todo hubiera salido bien.
        let msg = t('postAsset.submitError', 'No se pudo publicar el vehículo. Intenta de nuevo.');
        try {
          const data = await res.json();
          if (data?.detail) msg = typeof data.detail === 'string' ? data.detail : msg;
        } catch { /* respuesta sin JSON, usar msg por defecto */ }
        setUploadError(msg);
        return;
      }
      navigate('/provider/assets');
    } catch (error) {
      console.error('Error:', error);
      setUploadError(t('postAsset.connectionError', 'Error de conexión. Verifica tu internet e intenta de nuevo.'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <ProviderTabs />

      <div className="container mx-auto px-4 md:px-6 max-w-3xl py-8">
        <button
          onClick={() => navigate('/provider/dashboard')}
          className="mb-8 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition-colors"
        >
          {t('postAsset.backToPanel2')}
        </button>
        <h1 className="text-4xl font-heading font-bold mb-8">{t('postAsset.publishTitle')}</h1>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">{t('postAsset.type')}</label>
            <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]">
              {categories.map(c => (
                <option key={c.type} value={c.type}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('providerAssets.namelabel')}</label>
            <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('providerAssets.descriptionLabel')}</label>
            <textarea required value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="4" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"></textarea>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('postAsset.brand')}</label>
              <input type="text" value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('postAsset.model')}</label>
              <input type="text" value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('postAsset.year')}</label>
              <input type="number" required value={formData.year} onChange={(e) => setFormData({...formData, year: Number(e.target.value)})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('postAsset.capacity')}</label>
              <input type="number" required value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: Number(e.target.value)})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('postAsset.zone')}</label>
              <select value={formData.location_zone} onChange={(e) => setFormData({...formData, location_zone: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]">
                {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('providerAssets.pricePerHour')}</label>
              <input type="number" step="1" min="1" required value={formData.price_per_hour} onChange={(e) => setFormData({...formData, price_per_hour: Math.round(Number(e.target.value))})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('providerAssets.pricePerDay')}</label>
              <input type="number" step="1" min="1" required value={formData.price_per_day} onChange={(e) => setFormData({...formData, price_per_day: Math.round(Number(e.target.value))})} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('postAsset.vehicleImages')}</label>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
              <input
                type="file"
                accept="image/*,.jpg,.jpeg,.png,.webp"
                multiple
                onChange={handleImageFiles}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center gap-2 text-gray-500 hover:text-[#134168]">
                <span className="text-3xl">+</span>
                <span className="text-sm">{t('postAsset.uploadHint')}</span>
              </label>
              {uploading && <p className="text-sm text-gray-400 mt-2">{t('postAsset.uploading')}</p>}
              {uploadError && <p className="text-sm text-red-500 mt-2">{uploadError}</p>}
            </div>
            {uploadedPreviews.length > 0 && (
              <div className="flex gap-3 mt-3 flex-wrap">
                {uploadedPreviews.map((src, i) => (
                  <div key={i} className="relative">
                    <img src={src} alt="" className="w-24 h-24 object-cover rounded-xl border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                    >x</button>
                    {i === 0 && <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1 rounded">{t('postAsset.mainPhoto')}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-amber-50 p-4 rounded-xl flex gap-3 text-amber-800 text-sm mt-4 border border-amber-200">
            <span className="text-xl">ℹ️</span>
            <span>{t('postAsset.pendingWarning', 'Importante: Al publicar, tu vehículo pasará a estado de "Revisión". Un administrador verificará los datos antes de que sea visible en la plataforma para los clientes.')}</span>
          </div>
          <button type="submit" disabled={uploading} className="w-full bg-[#134168] text-white py-4 rounded-full font-bold text-lg hover:bg-[#39b4aa] transition-all disabled:opacity-50 mt-2">
            {t('postAsset.publishBtn')}
          </button>
        </form>
      </div>
    </div>
  );
}
